import { useAtomValue } from 'jotai';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import {
  latestSnapshotIdAtom,
  triggerHardRefetchAtom,
  portfolioIdAtom,
} from '~/atoms/dashboardAtom';
import { GET_PORTFOLIO_NAME } from '~/graphql/queries/portfolio';
import {
  endpointPortfolioConstituentBreakdownGET,
  type PortfolioConstituentBreakdownResponse,
} from '~/api/def/endpoints/portfolioAnalysisEndpoint';
import { AssetBreakdownWidget } from './AssetBreakdownWidget';

export const AssetBreakdownWidgetWrapper: React.FC<{
  handleRemoveWidget?: (widgetId: string) => void;
  widgetId: string;
  title?: string;
  scenario?: 'proposed' | 'current_holdings' | 'benchmark' | 'universe';
  horizonDays?: number;
}> = ({
  handleRemoveWidget,
  widgetId,
  title,
  scenario = 'proposed',
  horizonDays = 84,
}) => {
  const latestSnapshotId = useAtomValue(latestSnapshotIdAtom);
  const triggerHardRefetch = useAtomValue(triggerHardRefetchAtom);
  const portfolioId = useAtomValue(portfolioIdAtom);

  // Fetch portfolio name
  const { data: portfolioData } = useQuery(GET_PORTFOLIO_NAME, {
    variables: { portfolioId },
    skip: !portfolioId,
  });

  const portfolioName = useMemo(() => {
    return portfolioData?.EQPortfolio?.[0]?.name;
  }, [portfolioData]);

  const [breakdownData, setBreakdownData] =
    useState<PortfolioConstituentBreakdownResponse | null>(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [breakdownError, setBreakdownError] = useState<Error | null>(null);

  // Fetch asset breakdown data using REST API
  const fetchBreakdownData = useCallback(async () => {
    if (!latestSnapshotId) return null;

    try {
      setBreakdownLoading(true);
      setBreakdownError(null);

      const response = await endpointPortfolioConstituentBreakdownGET({
        snapshot_id: latestSnapshotId,
        scenario: scenario,
        horizon_days: horizonDays,
      });

      setBreakdownData(response as PortfolioConstituentBreakdownResponse);
      return response;
    } catch (error) {
      console.error('Failed to fetch asset breakdown data:', error);

      // Check if it's a 400 error (no data found)
      if (
        error &&
        typeof error === 'object' &&
        'statusCode' in error &&
        error.statusCode === 400
      ) {
        setBreakdownData(null);
        setBreakdownError(null);
      } else {
        setBreakdownError(error as Error);
      }
      return null;
    } finally {
      setBreakdownLoading(false);
    }
  }, [latestSnapshotId, scenario, horizonDays]);

  // Fetch breakdown data when snapshot changes
  useEffect(() => {
    if (!latestSnapshotId) return;
    fetchBreakdownData();
  }, [latestSnapshotId, fetchBreakdownData]);

  // Force hard refetch when triggered by other widgets
  useEffect(() => {
    if (triggerHardRefetch > 0) {
      fetchBreakdownData();
    }
  }, [triggerHardRefetch, fetchBreakdownData]);

  const refetch = useMemo(() => {
    return () => {
      fetchBreakdownData();
    };
  }, [fetchBreakdownData]);

  return (
    <AssetBreakdownWidget
      data={breakdownData}
      isLoading={breakdownLoading}
      error={breakdownError}
      refetch={refetch}
      handleRemoveWidget={handleRemoveWidget}
      widgetId={widgetId}
      title={title}
      snapshotId={latestSnapshotId}
      scenario={scenario}
      horizonDays={horizonDays}
      portfolioId={portfolioId}
      portfolioName={portfolioName}
    />
  );
};
